import { UnavailableDataPlaceholderText } from "constants/text.ts";
import { getOrderUpdateOrderId, ORDER_DETAILS_ROUTE, OrderUpdate } from "types/api/orders/getOrderUpdates.ts";
import { capitalizeFirstLetter } from "utils/string.ts";

export { getOrderUpdateOrderId as getActionItemOrderId, ORDER_DETAILS_ROUTE };

export type ActionItemGoTarget = { kind: "internal"; href: string } | { kind: "external"; href: string };

export const getActionItemInternalPath = (actionItem: Pick<OrderUpdate, "url">) => {
	const orderId = getOrderUpdateOrderId(actionItem);
	return orderId ? `${ORDER_DETAILS_ROUTE}/${encodeURIComponent(orderId)}` : undefined;
};

export const getSafeExternalActionItemUrl = (url: string | null | undefined) => {
	const value = url?.trim();
	if (!value) return undefined;

	try {
		const parsed = new URL(value);
		if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return undefined;
		return parsed.href;
	} catch {
		return undefined;
	}
};

/**
 * Order details urls navigate in-app to `/details/{lastPathSegment}` so state is kept.
 * Other http(s) urls do a full redirect.
 */
export const getActionItemGoTarget = (actionItem: Pick<OrderUpdate, "url">): ActionItemGoTarget | undefined => {
	const internalPath = getActionItemInternalPath(actionItem);
	if (internalPath) return { kind: "internal", href: internalPath };

	const externalUrl = getSafeExternalActionItemUrl(actionItem.url);
	return externalUrl ? { kind: "external", href: externalUrl } : undefined;
};

export const formatActionItemDescription = ({ description, orderNumber, accountNumber }: OrderUpdate) =>
	`${description ?? UnavailableDataPlaceholderText} - Order ${orderNumber ?? UnavailableDataPlaceholderText} for Store ${
		accountNumber ?? UnavailableDataPlaceholderText
	} needs review`;

/** PROCESSING_HOLD -> Processing Hold */
export const formatActionType = (statusReason: string | null | undefined) => {
	if (!statusReason) return UnavailableDataPlaceholderText;

	return statusReason
		.split("_")
		.filter(Boolean)
		.map((word) => capitalizeFirstLetter(word))
		.join(" ");
};

export const getActionItemDateSortValue = (dateString: string | null | undefined) => {
	if (!dateString) return 0;

	const timestamp = new Date(dateString).getTime();
	return Number.isNaN(timestamp) ? 0 : timestamp;
};

/** 2026-10-01T12:52:03.443 -> 10/01/2026 (reads the calendar date as-is so no timezone shifting occurs) */
export const formatActionItemDate = (dateString: string | null | undefined) => {
	if (!dateString) return UnavailableDataPlaceholderText;

	const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
	if (isoDateMatch) {
		const [, year, month, day] = isoDateMatch;
		return `${month}/${day}/${year}`;
	}

	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return UnavailableDataPlaceholderText;

	return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
};
